/**
 * Permission Engine - Project Permissions
 *
 * Handles project and subproject permission evaluation
 */

import {
  UserContext,
  PermissionRequirement,
  PermissionResult,
  ProjectMembership,
  PermissionAction,
  ProjectPermission,
  PROJECT_PERMISSION_HIERARCHY
} from '../types';
import { createSuccessResult, createErrorResult, isSystemAdmin } from './validation';
import { systemLogger } from '@/lib/logging';

/**
 * Evaluate project-specific permissions
 */
export function evaluateProjectPermission(
  user: UserContext,
  requirement: PermissionRequirement,
  projectMemberships: ProjectMembership[]
): PermissionResult {
  const { action, resource, resourceId } = requirement;

  // Handle PROJECT CREATE action (creating new projects) - no resourceId needed
  if (resource === 'project' && action === PermissionAction.CREATE && !resourceId) {
    return evaluateProjectCreationPermission(user);
  }

  if (!resourceId) {
    return createErrorResult('MISSING_PROJECT_ID', 'Project ID is required');
  }

  // Handle SUBPROJECT and REPORT creation (special business logic)
  if (resource === 'subproject' && action === PermissionAction.CREATE) {
    // Extract project status from requirement context if available
    const projectStatus = requirement.context?.['projectStatus'] as string | undefined;
    return evaluateSubprojectCreationPermission(user, resourceId, projectMemberships, projectStatus);
  }

  // Handle REPORT permissions (daily reports)
  if (resource === 'report') {
    // CLIENT company restriction: Cannot delete reports
    if (action === PermissionAction.DELETE && user.company_type === 'CLIENT') {
      return createErrorResult('CLIENT_COMPANY_RESTRICTION', 'CLIENT companies cannot delete reports');
    }

    // Handle REPORT creation with existing logic
    if (action === PermissionAction.CREATE) {
      return evaluateProjectReportCreationPermission(user, resourceId, projectMemberships);
    }

    // For all other REPORT actions, check project membership
    const membership = findProjectMembership(projectMemberships, resourceId, user.id);

    if (!membership) {
      return createErrorResult('NO_PROJECT_MEMBERSHIP', 'User is not a member of this project');
    }

    // For DELETE action, require EDITOR or OWNER level
    if (action === PermissionAction.DELETE) {
      const hasPermission = membership.permission_level === ProjectPermission.EDITOR ||
                           membership.permission_level === ProjectPermission.OWNER;

      if (hasPermission) {
        return createSuccessResult(
          'PROJECT_REPORT_DELETE_ALLOWED',
          `${membership.permission_level} can delete reports`
        );
      }

      return createErrorResult(
        'INSUFFICIENT_PROJECT_PERMISSION',
        `${membership.permission_level} cannot delete reports, requires EDITOR or OWNER`
      );
    }

    // For other actions, use standard project permission evaluation
    return evaluateProjectPermissionByLevel(action, membership.permission_level);
  }

  // Company Admins have automatic Editor permissions for projects in their company
  if (user.app_role === 'COMPANY_ADMIN') {
    // Apply CLIENT company edit restrictions for Company Admins too
    if (action === PermissionAction.EDIT && user.company_type === 'CLIENT') {
      const projectStatus = requirement.context?.['projectStatus'] as string | undefined;

      // DEBUG: Log CLIENT restriction evaluation
      systemLogger.debug('[CLIENT-EDIT-RESTRICTION] Evaluating for Company Admin', {
        userId: user.id,
        userCompanyType: user.company_type,  // Now from JWT!
        projectStatus,
        statusAllowed: projectStatus ? ['SENT', 'REJECTED'].includes(projectStatus) : 'no-status',
        willBlock: projectStatus && !['SENT', 'REJECTED'].includes(projectStatus)
      });

      if (projectStatus && !['SENT', 'REJECTED'].includes(projectStatus)) {
        systemLogger.debug('[CLIENT-EDIT-RESTRICTION] BLOCKING edit for CLIENT company on status', { projectStatus });
        return createErrorResult(
          'CLIENT_EDIT_RESTRICTION',
          `CLIENT company users can only edit projects when status is SENT or REJECTED, current status: ${projectStatus}`
        );
      }
    }
    return evaluateProjectPermissionByLevel(action, ProjectPermission.EDITOR);
  }

  // Find user's membership for this project
  const membership = findProjectMembership(projectMemberships, resourceId, user.id);

  if (!membership) {
    return createErrorResult('NO_PROJECT_MEMBERSHIP', 'User is not a member of this project');
  }

  // Check for CLIENT company edit restrictions
  if (action === PermissionAction.EDIT && user.company_type === 'CLIENT') {
    // CLIENT company users can only edit when status is SENT or REJECTED
    const projectStatus = requirement.context?.['projectStatus'] as string | undefined;

    // DEBUG: Log CLIENT restriction evaluation for regular users
    systemLogger.debug('[CLIENT-EDIT-RESTRICTION] Evaluating for regular user', {
      userId: user.id,
      userCompanyType: user.company_type,  // Now from JWT!
      membershipLevel: membership.permission_level,
      projectStatus,
      statusAllowed: projectStatus ? ['SENT', 'REJECTED'].includes(projectStatus) : 'no-status',
      willBlock: projectStatus && !['SENT', 'REJECTED'].includes(projectStatus)
    });

    if (projectStatus && !['SENT', 'REJECTED'].includes(projectStatus)) {
      systemLogger.debug('[CLIENT-EDIT-RESTRICTION] BLOCKING edit for CLIENT company user on status', { projectStatus });
      return createErrorResult(
        'CLIENT_EDIT_RESTRICTION',
        `CLIENT company users can only edit projects when status is SENT or REJECTED, current status: ${projectStatus}`
      );
    }
  }

  // Evaluate permission based on membership level
  return evaluateProjectPermissionByLevel(action, membership.permission_level);
}

/**
 * Evaluate permission based on project permission level
 */
function evaluateProjectPermissionByLevel(action: PermissionAction, permissionLevel: ProjectPermission): PermissionResult {
  const requiredLevel = getRequiredPermissionLevel(action);
  const userLevel = PROJECT_PERMISSION_HIERARCHY[permissionLevel];
  const requiredLevelValue = PROJECT_PERMISSION_HIERARCHY[requiredLevel];

  if (userLevel >= requiredLevelValue) {
    return createSuccessResult(
      'PROJECT_PERMISSION_GRANTED',
      `Permission level ${permissionLevel} allows ${action}`
    );
  }

  return createErrorResult(
    'INSUFFICIENT_PROJECT_PERMISSION',
    `Permission level ${permissionLevel} insufficient for ${action}, requires ${requiredLevel}`
  );
}

/**
 * Get required permission level for an action
 */
function getRequiredPermissionLevel(action: PermissionAction): ProjectPermission {
  switch (action) {
    case PermissionAction.VIEW:
      return ProjectPermission.VIEWER;
    case PermissionAction.CREATE:
    case PermissionAction.EDIT:
      return ProjectPermission.OPERATOR;
    case PermissionAction.DELETE:
      return ProjectPermission.EDITOR;
    case PermissionAction.APPROVE:
      return ProjectPermission.OWNER;
    default:
      return ProjectPermission.OWNER; // Most restrictive as fallback
  }
}

/**
 * Evaluate subproject creation permission
 * Business Rule: Only FACILITATOR companies with OWNER/EDITOR membership can create subprojects
 * Project must be ACCEPTED (not SENT, DONE, or REJECTED)
 */
function evaluateSubprojectCreationPermission(
  user: UserContext,
  projectId: string,
  projectMemberships: ProjectMembership[],
  projectStatus?: string
): PermissionResult {
  // DEBUG: Log subproject creation evaluation
  systemLogger.debug('[SUBPROJECT-CREATE] Evaluating permission', {
    userId: user.id,
    userRole: user.app_role,
    userCompanyType: user.company_type,  // Now from JWT!
    projectId,
    projectStatus,
    isSystemAdmin: isSystemAdmin(user)
  });

  // System Admin bypass - can always create subprojects (subject to project status)
  if (isSystemAdmin(user)) {
    // Check project status for System Admins too
    if (projectStatus) {
      const allowedStatuses = ['ACCEPTED', 'IN_PROGRESS_OK', 'IN_PROGRESS_LATE', 'IN_PROGRESS_INCIDENTS', 'IN_PROGRESS_STOPPED'];
      if (!allowedStatuses.includes(projectStatus)) {
        systemLogger.debug('[SUBPROJECT-CREATE] System Admin BLOCKED by status', { projectStatus });
        return createErrorResult(
          'PROJECT_STATUS_RESTRICTION',
          `Subprojects cannot be created for projects with status ${projectStatus}. Project must be ACCEPTED or IN_PROGRESS.`
        );
      }
    }
    systemLogger.debug('[SUBPROJECT-CREATE] System Admin ALLOWED');
    return createSuccessResult(
      'SYSTEM_ADMIN_SUBPROJECT_CREATION',
      'System Admin can create subprojects for projects in allowed status'
    );
  }

  // Get user's company type from JWT claims (denormalized for performance)
  const userCompanyType = user.company_type;

  // Only FACILITATOR companies can create subprojects
  if (userCompanyType !== 'FACILITATOR') {
    systemLogger.debug('[SUBPROJECT-CREATE] BLOCKED: Not FACILITATOR company', { userCompanyType });
    return createErrorResult(
      'COMPANY_TYPE_RESTRICTION',
      'Only FACILITATOR companies can create subprojects'
    );
  }

  // Find user's membership for this project
  const membership = findProjectMembership(projectMemberships, projectId, user.id);

  if (!membership) {
    systemLogger.debug('[SUBPROJECT-CREATE] BLOCKED: No project membership found');
    return createErrorResult('NO_PROJECT_MEMBERSHIP', 'User is not a member of this project');
  }

  // Check if user has sufficient project membership (OWNER or EDITOR)
  const hasProperMembership = membership.permission_level === ProjectPermission.OWNER ||
                              membership.permission_level === ProjectPermission.EDITOR;

  if (!hasProperMembership) {
    systemLogger.debug('[SUBPROJECT-CREATE] BLOCKED: Insufficient membership level', { level: membership.permission_level });
    return createErrorResult(
      'INSUFFICIENT_PROJECT_PERMISSION',
      'Subproject creation requires OWNER or EDITOR membership'
    );
  }

  // Check project status - only allow for ACCEPTED projects
  if (projectStatus) {
    const allowedStatuses = ['ACCEPTED', 'IN_PROGRESS_OK', 'IN_PROGRESS_LATE', 'IN_PROGRESS_INCIDENTS', 'IN_PROGRESS_STOPPED'];
    if (!allowedStatuses.includes(projectStatus)) {
      systemLogger.debug('[SUBPROJECT-CREATE] BLOCKED by status', { projectStatus, allowedStatuses });
      return createErrorResult(
        'PROJECT_STATUS_RESTRICTION',
        `Subprojects cannot be created for projects with status ${projectStatus}. Project must be ACCEPTED or IN_PROGRESS.`
      );
    }
  }

  systemLogger.debug('[SUBPROJECT-CREATE] ALLOWED for FACILITATOR company with proper membership');
  return createSuccessResult(
    'SUBPROJECT_CREATION_ALLOWED',
    'FACILITATOR company with proper membership can create subprojects'
  );
}

/**
 * Evaluate project report creation permission
 * Business Rule: Only FACILITATOR companies can create project reports
 * Project must be in active status (ACCEPTED, IN_PROGRESS_*)
 */
function evaluateProjectReportCreationPermission(
  user: UserContext,
  _projectId: string,
  _projectMemberships: ProjectMembership[]
): PermissionResult {
  // Get user's company type from JWT claims (denormalized for performance)
  const userCompanyType = user.company_type;

  // Only FACILITATOR companies can create project reports
  if (userCompanyType !== 'FACILITATOR') {
    return createErrorResult(
      'COMPANY_TYPE_RESTRICTION',
      'Only FACILITATOR companies can create project reports'
    );
  }

  // TODO: Add project status check when project data is available
  // const isProjectAccepted = project.status === 'ACCEPTED' ||
  //   project.status === 'IN_PROGRESS_OK' ||
  //   project.status === 'IN_PROGRESS_STOPPED' ||
  //   project.status === 'IN_PROGRESS_INCIDENTS' ||
  //   project.status === 'IN_PROGRESS_LATE';

  return createSuccessResult(
    'PROJECT_REPORT_CREATION_ALLOWED',
    'FACILITATOR company can create project reports'
  );
}

/**
 * Evaluate project creation permission
 * Business Rule: System Admins and Company Admins can create projects
 */
function evaluateProjectCreationPermission(user: UserContext): PermissionResult {
  // System Admin bypass - can always create projects
  if (isSystemAdmin(user)) {
    return createSuccessResult(
      'SYSTEM_ADMIN_PROJECT_CREATION',
      'System Admin can create projects'
    );
  }

  // Company Admins can create projects
  if (user.app_role === 'COMPANY_ADMIN') {
    return createSuccessResult(
      'COMPANY_ADMIN_PROJECT_CREATION',
      'Company Admin can create projects'
    );
  }

  // Regular users cannot create projects
  return createErrorResult(
    'PROJECT_CREATION_RESTRICTED',
    'Only System Admins and Company Admins can create projects'
  );
}

/**
 * Find project membership for user
 */
function findProjectMembership(
  memberships: ProjectMembership[],
  projectId: string,
  userId: string
): ProjectMembership | undefined {
  return memberships.find(m => m.assignment_id === projectId && m.user_id === userId);
}